import { envConfig } from '#constants/envConfig';

/**
 * JWT storage for the native (Capacitor) shell.
 *
 * The web app authenticates with the httpOnly `authToken` cookie set by the
 * gateway. The native shell has no cookie jar shared with the gateway, so it
 * stores the JWT itself (durably, in native Preferences) and sends it in the
 * `Accesstoken` header (the app's app.jwt.header-key) on GraphQL and as
 * `?token=` on the WebSocket.
 *
 * Everything here is gated behind `envConfig.IS_NATIVE` — a build-time constant
 * (`import.meta.env.VITE_IS_NATIVE === 'true'`). In the web build that folds to `false`,
 * so the dynamic `@capacitor/preferences` import is dead-code-eliminated and the
 * web bundle never pulls Capacitor in.
 */

const TOKEN_KEY = 'authToken';
/**
 * Id of the account the stored token belongs to, as the last `me` answered it. The app's data
 * snapshots (features/snapshot) are keyed by it: the launch has to know whose snapshot to restore
 * before any request is made. Written by `getMyProfile`, erased together with the token.
 */
const AUTH_USER_ID_KEY = 'authUserId';

// undefined = not yet loaded from storage; null = loaded, no token.
let cached: string | null | undefined;
// Same states for the account id.
let cachedUserId: string | null | undefined;
// Sign-out has begun and no token has been stored since (see beginNativeSignOut).
let signingOut = false;
const tokenListeners = new Set<() => void>();
const authReadyListeners = new Set<() => void>();

/** Verified `me` has persisted the account id belonging to the current token. */
export function onNativeAuthReady(listener: () => void): () => void {
  authReadyListeners.add(listener);
  return () => {
    authReadyListeners.delete(listener);
  };
}

export const isNativeShell = () => envConfig.IS_NATIVE;

const BRIDGE_WAIT_STEP_MS = 50;
const BRIDGE_WAIT_MAX_MS = 3_000;

/**
 * Waits for the Capacitor bridge to register its plugins.
 *
 * The WebView starts running page code before `window.Capacitor.Plugins` exists — measured at
 * ~180ms on a Mi 10T Pro. Asking Preferences inside that window answers "no value", which is
 * indistinguishable from "signed out": the router's auth guard then redirects to /sign-in while
 * a perfectly good token sits in storage. That is the "my account keeps logging itself out"
 * report. So we wait for the bridge rather than trust its silence.
 */
const hasPreferencesPlugin = () =>
  typeof window !== 'undefined' &&
  Boolean(
    (window as { Capacitor?: { Plugins?: { Preferences?: unknown } } }).Capacitor?.Plugins
      ?.Preferences
  );

async function waitForPreferencesPlugin(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  for (
    let waited = 0;
    waited < BRIDGE_WAIT_MAX_MS && !hasPreferencesPlugin();
    waited += BRIDGE_WAIT_STEP_MS
  ) {
    await new Promise((resolve) => setTimeout(resolve, BRIDGE_WAIT_STEP_MS));
  }
}

/**
 * Loads the token from durable storage on first call, then serves it from an
 * in-memory cache. Safe to call on every request.
 *
 * Only a real answer is cached. An empty read during startup used to be stored as "no token"
 * forever, so a single unlucky millisecond signed the user out for the rest of the session and
 * no later attempt could recover it.
 */
export async function getNativeToken(): Promise<string | null> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }
  if (cached !== undefined) {
    return cached;
  }
  try {
    await waitForPreferencesPlugin();
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value) {
      cached = value;
      return cached;
    }
    // Bridge answered and there is genuinely nothing stored — but don't freeze that in the
    // cache: a token can appear (sign-in, OAuth return) without this module being reloaded.
    return null;
  } catch {
    // Transient failure — leave the cache unset so the next call tries again.
    return null;
  }
}

/**
 * Synchronous read of the already-loaded token. Returns null until
 * {@link getNativeToken} has populated the cache — callers that can't await
 * (e.g. the WebSocket URL builder) must run after auth bootstrap, which they do
 * (the WS only opens once the `me` query — which awaits the token — has run).
 */
export function getCachedNativeToken(): string | null {
  return envConfig.IS_NATIVE ? (cached ?? null) : null;
}

/**
 * Drops the gateway's auth cookie from the WebView's jar.
 *
 * The shell was built on the assumption that it has no cookie jar shared with the gateway. It
 * does: the WebView issues ordinary browser requests, so the `Set-Cookie` from a sign-in is
 * stored and — being httpOnly, on the gateway's host rather than this app's origin — survives
 * every sign-out we can perform from here. The gateway used to read that cookie before our
 * header, so the app authenticated as whoever had signed in on this phone before: someone signed
 * in as themselves and got another account's profile, notifications and push registration
 * (06.08.2026).
 *
 * The gateway now prefers the header, so this is the second line of defence — but it is the one
 * that acts immediately, without waiting for a deploy, and it keeps the invariant honest: in the
 * native shell the stored token is the session, and nothing ambient may speak for it.
 */
export async function clearNativeAuthCookie(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    return;
  }
  try {
    const { CapacitorCookies } = await import('@capacitor/core');
    const urls = new Set<string>();
    if (envConfig.API_URL) {
      urls.add(new URL(envConfig.API_URL).origin);
    }
    if (typeof window !== 'undefined') {
      urls.add(window.location.origin);
    }
    // Only our own key: the jar also holds analytics and utm cookies that have nothing to do
    // with the session, and clearing everything would quietly reset them on every sign-in.
    await Promise.all(
      [...urls].map((url) => CapacitorCookies.deleteCookie({ url, key: TOKEN_KEY }))
    );
  } catch {
    // best-effort — the header is what actually authenticates us
  }
}

/**
 * The account id stored next to the token, or null when there is none or storage can't be read.
 * Waits for the bridge the same way {@link getNativeToken} does: the launch asks before it is up.
 */
export async function getNativeAuthUserId(): Promise<string | null> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }
  if (cachedUserId !== undefined) {
    return cachedUserId;
  }
  try {
    await waitForPreferencesPlugin();
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: AUTH_USER_ID_KEY });
    cachedUserId = value || null;
    return cachedUserId;
  } catch {
    return null;
  }
}

/** Remembers whose token is stored. Called with every profile `me` answers; writes only on change. */
export async function setNativeAuthUserId(userId: string): Promise<void> {
  if (!envConfig.IS_NATIVE || !userId || cachedUserId === userId) {
    return;
  }
  cachedUserId = userId;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: AUTH_USER_ID_KEY, value: userId });
  } catch {
    // best-effort: without it the next launch simply starts without a snapshot
  }
  for (const listener of authReadyListeners) listener();
}

async function forgetNativeAuthUserId(): Promise<void> {
  cachedUserId = null;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: AUTH_USER_ID_KEY });
  } catch {
    // best-effort
  }
}

/**
 * Sign-out has begun. Until a token is stored again, an answer of the leaving session that is
 * still in flight (a `me` sent a moment before) must write nothing back to the phone - see
 * {@link isCurrentNativeSession}. Without this, such an answer put the leaving account's id (and,
 * once a day, its refreshed token) back into storage after the sign-out had wiped them.
 */
export function beginNativeSignOut(): void {
  if (envConfig.IS_NATIVE) {
    signingOut = true;
  }
}

/**
 * Runs the listener every time a token is stored: a sign-in (password, registration, OAuth
 * return) or the daily refresh of the current session. A refresh never lands during a sign-out
 * (see {@link isCurrentNativeSession}), so after a sign-out the next call is a sign-in.
 */
export function onNativeTokenStored(listener: () => void): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}

/**
 * Which session a request is about to run in: the token it will carry. Compare it with
 * {@link isCurrentNativeSession} once the answer is back. Web: always null.
 */
export async function nativeSessionMark(): Promise<string | null> {
  return envConfig.IS_NATIVE ? getNativeToken() : null;
}

/**
 * Whether an answer still belongs to the session its request ran in: no sign-out has begun and
 * the stored token is the one the request carried. Web: always true.
 */
export function isCurrentNativeSession(mark: string | null): boolean {
  if (!envConfig.IS_NATIVE) {
    return true;
  }
  return !signingOut && (cached ?? null) === mark;
}

export async function setNativeToken(token: string): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    return;
  }
  cached = token;
  signingOut = false;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch {
    // best-effort; the in-memory cache still serves this session
  }
  // A new token may belong to another account (sign-in, OAuth return), and until `me` says whose
  // it is, no snapshot may be restored for it. A refreshed token of the same account gets its id
  // back a moment later, from the very `me` answer that carried it.
  await forgetNativeAuthUserId();
  // A fresh session must not run behind the cookie of the previous one.
  await clearNativeAuthCookie();
  for (const listener of tokenListeners) {
    listener();
  }
}

export async function clearNativeToken(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    return;
  }
  cached = null;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: TOKEN_KEY });
  } catch {
    // best-effort
  }
  await forgetNativeAuthUserId();
  // Signing out has to end the session on every channel that can speak for it — see
  // clearNativeAuthCookie. The web's own logout cannot reach this cookie: it deletes cookies on
  // the frontend host, and in the shell the frontend is https://localhost while the session
  // cookie belongs to the gateway's domain.
  await clearNativeAuthCookie();
}

/**
 * Persists the JWT from an auth mutation result (login / register /
 * confirmRegistration / resetPassword) when running in the native shell. On web
 * the gateway's Set-Cookie handles this, so it's a no-op. Accepts the raw union
 * result — only acts when it's a `Token`.
 */
export async function persistNativeAuthResult(
  result: { __typename?: string; token?: string | null } | null | undefined
): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    return;
  }
  if (result?.__typename === 'Token' && result.token) {
    await setNativeToken(result.token);
  }
}
